<template>
  <div
    v-if="element"
    class="main-component-multi-input-selector"
  >
    <div
      class="mode-button"
      @click="changeMode"
    >
      <img
        :src="element.iconButton"
        :alt="element.altButton"
        class="button-icon"
      >
    </div>

    <form
      class="form"
      @submit.prevent="validateInput"
    >
      <input
        :id="inputElementId"
        type="text"
        class="input"
        :placeholder="element.placeholder"
        :value="inputValue"
        @input="inputValue = /** @type {HTMLInputElement} */ ($event.target).value"
      >
    </form>

    <div
      v-show="element.showBarCodeButton"
      class="qr-button"
    >
      <img
        src="@/static/icons/qr-code.svg"
        alt="iconQrCode"
        class="button-icon-qr"
        @click="openCodeScanner"
      >
    </div>

    <UCCodeScanner
      v-if="element.showBarCodeButton || element.confReadBarCode"
      ref="codeScannerRef"
      :active="true"
      :fab-button-show="element.confReadBarCode?.fabButtonShow ?? defaultBarCodeSearchParams.fabButtonShow"
      :fab-button-background-color="element.confReadBarCode?.fabButtonBackgroundColor ?? defaultBarCodeSearchParams.fabButtonBackgroundColor"
      :fab-button-min-width="element.confReadBarCode?.fabButtonMinWidth"
      :fab-button-text="element.confReadBarCode?.fabButtonText"
      :allow-manual-code-entry="element.confReadBarCode?.allowManualCodeEntry"
      :auto-open-camera="element.confReadBarCode?.autoOpenCamera ?? defaultBarCodeSearchParams.autoOpenCamera"
      :autofocus-on-input="element.confReadBarCode?.autofocusOnInput ?? defaultBarCodeSearchParams.autofocusOnInput"
      :continuous-bar-code-reading="element.confReadBarCode?.continuousBarCodeReading ?? defaultBarCodeSearchParams.continuousBarCodeReading"
      :orientation-scan="element.confReadBarCode?.orientationScan ?? defaultBarCodeSearchParams.orientationScan"
      :torch-on="element.confReadBarCode?.torchOn ?? defaultBarCodeSearchParams.torchOn"
      @code-reading="handleCodeRead"
      @popup-close="emitCodeScannerClose"
    />
  </div>
</template>

<script setup>
// @ts-check
import { computed, ref, shallowRef } from 'vue';
import UCCodeScanner from './UCCodeScanner.vue';

/** @import { PropType, ShallowRef } from 'vue' */

/** @typedef {{
 *  fabButtonBackgroundColor: string;
 *  fabButtonShow: boolean;
 *  fabButtonMinWidth: string;
 *  fabButtonText: string;
 *  allowManualCodeEntry: boolean;
 *  barcodeSearchPrefix: string;
 *  autoOpenCamera: boolean;
 *  autofocusOnInput: boolean;
 *  continuousBarCodeReading: boolean;
 *  orientationScan: 'portrait' | 'landscape';
 *  torchOn: boolean;
}} MultiInputSelectorConfProps */

/** @typedef {{
 * id: string;
 * placeholder: string;
 * iconButton: any;
 * altButton?: string;
 * showBarCodeButton?: boolean;
 * confReadBarCode?: Partial<MultiInputSelectorConfProps>
 * }} MultiInputSelectorConf */

const props = defineProps({
  conf: {
    type: /** @type {PropType<MultiInputSelectorConf[]>} */ (Array),
    required: true,
  },
  // Limpia el valor del input al validar.
  clearOnValidate: {
    type: Boolean,
    default: true,
  },
  // Permite utilizar el botón de cambio entre input.
  allowChange: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'change',
  'openReadCode',
  'codeScannerRead',
  'validateInput',
  'codeScannerClose',
]);

const inputValue = ref('');
// Índice actual en la configuración
const confIndex = ref(0);
/** @type {ShallowRef<InstanceType<typeof UCCodeScanner> | null>} */
const codeScannerRef = shallowRef(null);

const defaultBarCodeSearchParams = /** @satisfies {Partial<MultiInputSelectorConfProps>} */ ({
  fabButtonBackgroundColor: '',
  fabButtonShow: false,
  // Permite que las busquedas se realicen añadiendo un prefijo al inicio ejemplo: code:000001
  barcodeSearchPrefix: '',
  autoOpenCamera: true,
  autofocusOnInput: false,
  continuousBarCodeReading: false,
  orientationScan: 'portrait',
  torchOn: true,
});

const element = computed(() => props.conf[confIndex.value]);
const inputElementId = computed(() => Math.random().toString());

function changeMode() {
  if (!props.allowChange) {
    return;
  }

  confIndex.value = (confIndex.value + 1) % props.conf.length;
  inputValue.value = ''; // Limpiar el campo de entrada al cambiar el modo
  emit('change', element.value);
}

function openCodeScanner() {
  emit('openReadCode');
  codeScannerRef.value?.Open();
}

/**
 * @param {string} val
 * @returns {void}
 */
function handleCodeRead(val) {
  emit('codeScannerRead', val);
  inputValue.value = val;
  validateInput();
}

function emitCodeScannerClose() {
  emit('codeScannerClose');
}

function validateInput() {
  emit('validateInput', inputValue.value);
  if (props.clearOnValidate) {
    inputValue.value = '';
  }
}

/**
 * Permite seleccionar uno de los metodos de entrada.
 * @public
 * @param {string} findElementId
 * @param {boolean} emitEvent
 * @returns {void}
 */
function selectEntry(findElementId, emitEvent) {
  confIndex.value = props.conf.findIndex((e) => e.id === findElementId);
  inputValue.value = ''; // Limpiar el campo de entrada al cambiar el modo
  if (emitEvent) {
    emit('change', element.value);
  }
}

/**
 * @public
 * @returns {void}
 */
function focus() {
  document.getElementById(inputElementId.value)?.focus();
}

defineExpose({
  focus,
  selectEntry,
});
</script>

<style scoped lang="scss">
.main-component-multi-input-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px;

  .form {
    width: 100%;

    .input {
      flex: 1;
      height: 60px;
      border: 1px solid var(--his-silver);
      border-radius: 8px;
      padding: 0 16px;
      font-size: 16px;
      margin: 0 16px;
      background-color: #fff;
      width: 94%;
    }
  }

  .button-icon {
    width: 45px;
    height: 45px;
  }

  .button-icon-qr {
    width: 40px;
    height: 40px;
    padding-left: 9px;
  }

  .mode-button,
  .qr-button {
    background: var(--his-tierra-white);
    border: 1px solid var(--his-silver);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    box-sizing: content-box;
  }
}
</style>
